import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  FileSpreadsheet,
  FileText,
  CheckSquare,
  Square,
  FileDown,
  UploadCloud,
  RefreshCcw,
  Activity,
  ExternalLink,
} from "lucide-react";

export default function SubmissionsList() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [selected, setSelected] = useState(new Set());
  const [pushing, setPushing] = useState(new Set());
  const [health, setHealth] = useState(null); // {emd, ai}
  const [me, setMe] = useState(null);

  const load = () =>
    api.get("/api/admin/submissions?limit=200").then((r) => setData(r.data));

  useEffect(() => {
    load();
    api.get("/api/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const canWrite = me && (me.role === "superadmin" || me.role === "admin");

  const base = process.env.REACT_APP_BACKEND_URL;
  const csvUrl = `${base}/api/admin/submissions.csv`;
  const xlsxAllUrl = `${base}/api/admin/submissions.xlsx`;
  const xlsxSelectedUrl =
    selected.size > 0
      ? `${base}/api/admin/submissions.xlsx?ids=${Array.from(selected).join(",")}`
      : null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((s) => s.id)));
  };
  const allChecked =
    data.items.length > 0 && selected.size === data.items.length;

  const showProfileScaleColumn = data.items.some(
    (s) => s.scoring_profile_is_default === false
  );

  const openDeck = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.get(
        `/api/admin/submissions/${id}/deck/download`
      );
      if (data?.download_url) window.open(data.download_url, "_blank", "noopener");
    } catch (err) {
      alert(err.response?.data?.detail || "Не удалось получить ссылку на дек");
    }
  };

  const pushToEmd = async (id, force = false) => {
    setPushing((prev) => new Set(prev).add(id));
    try {
      const url = force
        ? `/api/admin/submissions/${id}/export-to-emd?force=true`
        : `/api/admin/submissions/${id}/export-to-emd`;
      await api.post(url);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Не удалось отправить в EMD Cloud");
      await load();
    } finally {
      setPushing((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const checkHealth = async () => {
    setHealth({ loading: true });
    try {
      const [emd, ai] = await Promise.all([
        api.get("/api/admin/emd-cloud/health").then((r) => r.data).catch((e) => ({ ok: false, error: e.message })),
        api.get("/api/admin/ai/health").then((r) => r.data).catch((e) => ({ ok: false, error: e.message })),
      ]);
      setHealth({ emd, ai });
    } catch (_) {
      setHealth({ error: "Не удалось выполнить проверку" });
    }
  };

  const emdBadge = (emd) => {
    if (!emd) return <span className="text-foreground/40">—</span>;
    if (emd.status === "ok")
      return (
        <div>
          <span
            className="inline-block px-2 py-0.5 border border-green-600/50 text-green-700 text-xs"
            title={`Row: ${emd.emd_row_id || "—"} · ${emd.created_at || ""}`}
            data-testid="emd-status-ok"
          >
            OK
          </span>
          {emd.emd_row_id && (
            <div className="text-[10px] text-foreground/40 mono mt-0.5" title="EMD row id">
              {emd.emd_row_id.slice(-6)}
            </div>
          )}
        </div>
      );
    if (emd.status === "error")
      return (
        <div>
          <span
            className="inline-block px-2 py-0.5 border border-red-500/50 text-red-700 text-xs"
            title={emd.error || ""}
            data-testid="emd-status-error"
          >
            Ошибка
          </span>
          {emd.error && (
            <div className="text-[10px] text-red-600/80 mt-0.5 max-w-[180px] truncate" title={emd.error}>
              {emd.error}
            </div>
          )}
        </div>
      );
    if (emd.status === "skipped") {
      const reason = emd.reason || emd.error || "";
      const isNoChanges = /no changes|hash/i.test(reason);
      const isNoToken = /token/i.test(reason);
      const short = isNoChanges
        ? "без изменений"
        : isNoToken
        ? "нет токена"
        : reason.slice(0, 40) || "нет причины";
      return (
        <div>
          <span
            className="inline-block px-2 py-0.5 border border-foreground/15 text-foreground/60 text-xs"
            title={reason}
            data-testid="emd-status-skipped"
          >
            Пропущено
          </span>
          <div className="text-[10px] text-foreground/50 mt-0.5 max-w-[180px] truncate" title={reason}>
            {short}
          </div>
        </div>
      );
    }
    return <span className="text-foreground/40">{emd.status}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-mono">САБМИТЫ</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2">
            {data.total} <span className="text-foreground/40">всего</span>
            {selected.size > 0 && (
              <span
                className="ml-3 text-base text-foreground/60"
                data-testid="selected-count"
              >
                · выбрано: {selected.size}
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canWrite && (
            <button
              onClick={checkHealth}
              className="btn-ghost"
              data-testid="health-check-btn"
              title="Проверить подключение к EMD Cloud и OpenAI"
            >
              <Activity size={14} /> Health-check
            </button>
          )}
          {canWrite && (
            <button
              onClick={async () => {
                if (!window.confirm("Пересчитать confidence на всех сабмитах? Обновит записи, где ai_extraction_status упал, но дек обработан. Быстро и безопасно.")) return;
                try {
                  const r = await api.post("/api/admin/submissions/backfill-confidence");
                  alert(`Готово. Проверено: ${r.data.total_low_before}, обновлено на 'normal': ${r.data.updated_to_normal}.`);
                  await load();
                } catch (err) {
                  alert(err.response?.data?.detail || "Ошибка");
                }
              }}
              className="btn-ghost"
              data-testid="backfill-confidence-btn"
              title="One-shot: пересчитывает confidence для существующих сабмитов по актуальной логике (учитывает статус дека)"
            >
              <RefreshCcw size={14} /> Обновить confidence
            </button>
          )}
          <a href={csvUrl} className="btn-ghost" data-testid="export-csv-link">
            <FileText size={14} /> CSV
          </a>
          {xlsxSelectedUrl ? (
            <a
              href={xlsxSelectedUrl}
              className="btn-primary"
              data-testid="export-xlsx-selected-link"
            >
              <FileSpreadsheet size={14} /> Excel ({selected.size})
            </a>
          ) : (
            <a
              href={xlsxAllUrl}
              className="btn-secondary"
              data-testid="export-xlsx-link"
            >
              <FileSpreadsheet size={14} /> Excel все
            </a>
          )}
        </div>
      </div>

      {health && (
        <div
          className="border border-black/10 bg-secondary/40 p-4 mb-6 text-sm"
          data-testid="health-result"
        >
          {health.loading && <div className="text-foreground/60">Проверяю…</div>}
          {health.emd && (
            <div className="flex items-center gap-3 mb-1">
              <span className="label-mono w-20">EMD:</span>
              {health.emd.ok ? (
                <span className="text-green-700">✓ {health.emd.app_title}</span>
              ) : (
                <span className="text-red-700">
                  ✕ {health.emd.error || health.emd.status || "error"}
                </span>
              )}
            </div>
          )}
          {health.ai && (
            <div className="flex items-center gap-3">
              <span className="label-mono w-20">OpenAI:</span>
              {health.ai.ok ? (
                <span className="text-green-700">
                  ✓ {health.ai.model} · {health.ai.latency_ms} ms
                </span>
              ) : (
                <span className="text-red-700">
                  ✕ {(health.ai.error || "error").slice(0, 180)}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-black/10">
            <tr className="label-mono">
              <th className="px-3 py-3 w-8">
                <button
                  onClick={toggleAll}
                  data-testid="select-all-btn"
                  className="inline-flex items-center"
                >
                  {allChecked ? <CheckSquare size={14} /> : <Square size={14} />}
                </button>
              </th>
              <th className="text-left px-4 py-3">Создано</th>
              <th className="text-left px-4 py-3">Стартап</th>
              <th className="text-left px-4 py-3">Контакт</th>
              <th className="text-left px-4 py-3">Тип</th>
              <th className="text-left px-4 py-3">Дек</th>
              <th className="text-right px-4 py-3">ИНВ</th>
              <th className="text-right px-4 py-3">КОРП</th>
              <th className="text-right px-4 py-3">ИНСТ</th>
              <th className="text-left px-4 py-3">Профиль</th>
              {showProfileScaleColumn && (
                <th className="text-left px-4 py-3">Шкала</th>
              )}
              <th className="text-left px-4 py-3">EMD</th>
              {canWrite && <th className="text-right px-4 py-3">Действия</th>}
            </tr>
          </thead>
          <tbody data-testid="submissions-table-body">
            {data.items.length === 0 && (
              <tr>
                <td
                  colSpan={showProfileScaleColumn ? 13 : 12}
                  className="px-4 py-8 text-center text-foreground/50"
                >
                  Пока нет сабмитов
                </td>
              </tr>
            )}
            {data.items.map((s) => (
              <tr
                key={s.id}
                className="border-b border-black/5 hover:bg-secondary/50"
              >
                <td className="px-3 py-3 align-middle">
                  <button
                    onClick={() => toggle(s.id)}
                    data-testid={`select-submission-${s.id}`}
                    className="inline-flex items-center"
                  >
                    {selected.has(s.id) ? (
                      <CheckSquare size={14} />
                    ) : (
                      <Square size={14} />
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 mono text-xs text-foreground/70">
                  {(s.created_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/submissions/${s.id}`}
                    className="hop-link"
                    data-testid={`submission-row-${s.id}`}
                  >
                    {s.startup_name}
                  </Link>
                  {s.share_token && (
                    <a
                      href={`/share/${s.share_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center text-foreground/40 hover:text-foreground/80 align-middle"
                      title="Открыть публичную страницу результата (та же ссылка, что получает основатель)"
                      data-testid={`share-open-${s.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                  {typeof s.verification_alerts === "number" && s.verification_alerts > 0 && (
                    <span
                      className="ml-2 inline-block px-1.5 py-0.5 border border-amber-500/60 text-amber-700 text-[10px] mono align-middle"
                      title={`AI-верификация: ${s.verification_alerts} противоречий с ответами основателя`}
                      data-testid={`verification-alerts-${s.id}`}
                    >
                      ⚠ {s.verification_alerts}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-foreground/70">
                  {s.founder_email}
                </td>
                <td className="px-4 py-3 mono text-xs">
                  <span
                    className={`inline-block px-2 py-0.5 border ${
                      s.questionnaire_type === "extended"
                        ? "border-foreground/40"
                        : "border-foreground/15"
                    }`}
                  >
                    {s.questionnaire_type === "extended" ? "EXT" : "MVP"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {s.has_deck ? (
                    <button
                      onClick={(e) => openDeck(e, s.id)}
                      className="hop-link inline-flex items-center gap-1"
                      data-testid={`deck-open-${s.id}`}
                      title="Открыть питч-дек в новой вкладке"
                    >
                      <FileDown size={12} /> Дек
                    </button>
                  ) : (
                    <span className="text-foreground/40">—</span>
                  )}
                </td>
                <td className="px-4 py-3 mono text-right">
                  {s.indices?.investor?.value ?? "—"}
                </td>
                <td className="px-4 py-3 mono text-right">
                  {s.indices?.corporate?.value ?? "—"}
                </td>
                <td className="px-4 py-3 mono text-right">
                  {s.indices?.institutional?.value ?? "—"}
                </td>
                <td className="px-4 py-3 text-xs">{s.profile_label || "—"}</td>
                {showProfileScaleColumn && (
                  <td className="px-4 py-3 text-xs text-foreground/60">
                    {s.scoring_profile_is_default === false
                      ? s.scoring_profile_name
                      : ""}
                  </td>
                )}
                <td className="px-4 py-3">{emdBadge(s.emd_export)}</td>
                {canWrite && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={(e) => pushToEmd(s.id, e.shiftKey)}
                        disabled={pushing.has(s.id)}
                        className="btn-ghost text-xs"
                        data-testid={`emd-push-${s.id}`}
                        title="Отправить в EMD Cloud. Shift+клик — принудительная перевыгрузка (создаст новую строку даже если данные не изменились)"
                      >
                        {s.emd_export?.status === "error" ? (
                          <>
                            <RefreshCcw size={12} /> Retry
                          </>
                        ) : (
                          <>
                            <UploadCloud size={12} />{" "}
                            {pushing.has(s.id) ? "…" : "EMD"}
                          </>
                        )}
                      </button>
                      {s.emd_export?.status === "skipped" && (
                        <button
                          onClick={() => pushToEmd(s.id, true)}
                          disabled={pushing.has(s.id)}
                          className="btn-ghost text-xs opacity-60 hover:opacity-100"
                          data-testid={`emd-force-push-${s.id}`}
                          title="Принудительно создать новую строку в EMD (обходит идемпотентность)"
                        >
                          Force
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
